import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';

import { EnumNgEventName } from '../../shared/event-animation.data';
import { NgContinuousAnimationService } from '../../shared/services/continuous-animation.service';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function pulse(minScale: number | undefined, maxScale: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	minScale = minScale ? minScale : 0.9;
	maxScale = maxScale ? maxScale : 1.1;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: 'scale3d(1, 1, 1)',
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `scale3d(${maxScale}, ${maxScale}, ${maxScale})`,
				easing 		: 'ease',
				offset 		: 0.25
				}),
			style({
				transform 	: `scale3d(${minScale}, ${minScale}, ${minScale})`,
				easing 		: 'ease',
				offset 		: 0.75
			}),
			style({
				transform 	: 'scale3d(1, 1, 1)',
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Float on Event
 */
@Directive({
    selector: '[ngPulseOnEvent]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgPulseOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the minScale & maxScale values are relative to the animation functionality
		if (this.effectOptions?.minScale && this.effectOptions?.minScale > 1) throw new Error(`PulseOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be lower than 1: ${this.effectOptions?.minScale}`);
		if (this.effectOptions?.maxScale && this.effectOptions?.maxScale < 1) throw new Error(`PulseOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be greater than 1: ${this.effectOptions?.maxScale}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pulse(this.effectOptions?.minScale, this.effectOptions?.maxScale),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Float on Mouse Hover
 */
@Directive({
    selector: '[ngPulseOnMouseHover]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgPulseOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 -  check the minScale & maxScale values are relative to the animation functionality
		if (this.timingOptions?.minScale && this.timingOptions?.minScale > 1) throw new Error(`PulseOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided scale value should be lower than 1: ${this.timingOptions?.minScale}`);
		if (this.timingOptions?.maxScale && this.timingOptions?.maxScale < 1) throw new Error(`PulseOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided scale value should be greater than 1: ${this.timingOptions?.maxScale}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pulse(this.timingOptions?.minScale, this.timingOptions?.maxScale),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	minScale : number;
	maxScale : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	minScale : number;
	maxScale : number;
}
